import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LIVE_CALL_STATUSES, type Call } from '@fsm/shared';
import { api } from './api';
import { useAuth } from './auth';

/**
 * Live call state for the office.
 *
 * Server-sent events push ringing calls to every open tab. Two things make
 * this survive a real day:
 *
 *   1. **It reconnects.** Laptops sleep, wifi drops, proxies cull idle
 *      connections. Backoff is capped low — thirty seconds without a screen-pop
 *      is thirty seconds of a dispatcher answering blind.
 *   2. **It re-syncs on reconnect.** An event that arrived while the socket was
 *      down is simply gone, so every reconnect refetches the live calls rather
 *      than trusting the stream to have been complete.
 *
 * A dismissed call stays dismissed for the life of the tab: closing the pop
 * means "I have dealt with this", and it must not reappear when the next event
 * for that call lands.
 */

interface CallState {
  /** Calls ringing or connected now, minus anything dismissed here. */
  liveCalls: Call[];
  connected: boolean;
  dismiss: (callId: string) => void;
  /** Most recent inbound call, dismissed or not. Used by the call log badge. */
  lastInbound: Call | null;
}

const CallContext = createContext<CallState | null>(null);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;

export function CallProvider({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  const qc = useQueryClient();

  const [calls, setCalls] = useState<Call[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [connected, setConnected] = useState(false);
  const [lastInbound, setLastInbound] = useState<Call | null>(null);

  const isOffice = user?.role === 'admin' || user?.role === 'dispatcher';

  const upsert = useCallback((call: Call) => {
    setCalls((current) => {
      const without = current.filter((c) => c.id !== call.id);
      // Only live calls belong in this list; an ended call drops straight out.
      if (!LIVE_CALL_STATUSES.includes(call.status)) return without;
      return [call, ...without].slice(0, 5);
    });
    if (call.direction === 'inbound') setLastInbound(call);
  }, []);

  /** Pull the current truth. Called on connect and on every reconnect. */
  const resync = useCallback(async () => {
    try {
      const result = await api.calls.live();
      setCalls(result.items.slice(0, 5));
    } catch {
      // Offline or unauthorised; the stream will retry and so will this.
    }
  }, []);

  const retries = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const source = useRef<EventSource | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || !isOffice) return;
    let cancelled = false;

    const connect = async () => {
      if (cancelled) return;
      // Not `tokens.get()`: that returns whatever is in the store, expired or
      // not, and the stream has no way to refresh mid-flight. Asking for a
      // fresh one here is what stops a long-open dispatch board reconnecting
      // for ever against a token that died fifteen minutes into the shift.
      const accessToken = await api.freshAccessToken();
      if (!accessToken || cancelled) return;

      const es = new EventSource(api.calls.streamUrl(accessToken));
      source.current = es;

      es.addEventListener('ready', () => {
        if (cancelled) return;
        retries.current = 0;
        setConnected(true);
        void resync();
      });

      const onCall = (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data) as { call?: Call };
          if (payload.call) upsert(payload.call);
        } catch {
          /* a malformed frame is not worth tearing the stream down for */
        }
        // A finished call changes history, and the customer's timeline with it.
        void qc.invalidateQueries({ queryKey: ['calls'] });
      };

      for (const type of ['call.ringing', 'call.answered', 'call.ended', 'call.updated']) {
        es.addEventListener(type, onCall as EventListener);
      }

      es.addEventListener('message.received', () => {
        void qc.invalidateQueries({ queryKey: ['messages'] });
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        source.current = null;
        if (cancelled) return;
        // Exponential backoff with a low ceiling and jitter, so twenty tabs
        // reconnecting after a proxy blip do not arrive in lockstep.
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** retries.current, RECONNECT_MAX_MS);
        retries.current += 1;
        timer.current = setTimeout(connect, delay + Math.random() * 500);
      };
    };

    void connect();

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
      source.current?.close();
      source.current = null;
      setConnected(false);
    };
  }, [status, isOffice, resync, upsert, qc]);

  const dismiss = useCallback((callId: string) => {
    setDismissed((current) => new Set(current).add(callId));
  }, []);

  const value = useMemo<CallState>(
    () => ({
      liveCalls: calls.filter((c) => !dismissed.has(c.id)),
      connected,
      dismiss,
      lastInbound,
    }),
    [calls, dismissed, connected, dismiss, lastInbound],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCalls(): CallState {
  const ctx = useContext(CallContext);
  // Rendered outside the provider (a tech's session, say) it simply reports
  // nothing rather than throwing.
  return (
    ctx ?? {
      liveCalls: [],
      connected: false,
      dismiss: () => {},
      lastInbound: null,
    }
  );
}
